import {
  FloatingIndicator,
  Tabs,
  type MantineBreakpoint,
} from '@mantine/core';
import {
  useThrottledCallback,
} from '@mantine/hooks';
import {
  useMatches,
  useNavigate,
  useRouter,
  type LinkComponentProps,
  type MakeRouteMatchUnion,
} from '@tanstack/react-router';
import React, {
  useMemo,
  useState,
} from 'react';

import tabsGroupStyles from '#components/ui/nav/TabsGroup.module.scss';
import {
  type RootRouterContext,
} from '#src/types/routerContext';

export interface TabProps {
  label: string;
  link: LinkComponentProps;
  hidden?: boolean | ((context?: RootRouterContext) => boolean);
}

export interface TabsProps {
  tabs: Array<TabProps>;
  defaultTabIndex?: number;
  visibleFrom?: MantineBreakpoint;
}

export type TabKeyType = keyof TabsProps['tabs'];

export function TabsGroup(
  props: TabsProps,
): React.JSX.Element {
  const _router = useRouter();
  type MatchesType = Array<MakeRouteMatchUnion<typeof _router>>;
  const fallbackTabIndex = props.defaultTabIndex || 0;
  const [
    value,
    setValue,
  ] = useState<number>(fallbackTabIndex);
  function selectActiveTabByRoute(
    tabs: Array<TabProps>,
    matches: MatchesType,
  ) {
    if (matches && tabs) {
      const activeTabIndex = tabs.map((tab, tabIndex) => {
        return {
          tabIndex,
          matchLength: matches.filter(match => tab.link?.to?.startsWith(match.pathname)).length,
        };
      }).reduce(
        (
          maxIndex,
          currentValue,
          currentIndex,
          array,
        ) => {
          if (currentValue.matchLength > array[maxIndex].matchLength) {
            return currentIndex;
          } else {
            return maxIndex;
          }
        },
        0,
      );
      const result = {
        tab: props.tabs[activeTabIndex],
        tabIndex: activeTabIndex,
      };
      // console.debug('TabsGroup selectActiveTabByRoute:', result);
      if (activeTabIndex !== value) {
        setValue(activeTabIndex);
      }
      return result;
    } else {
      const result = {
        tab: props.tabs[fallbackTabIndex],
        tabIndex: fallbackTabIndex,
      };
      console.debug('TabsGroup selectActiveTabByRoute using fallback:', result);
      return result;
    }
  }
  const throttledSelectActiveTabByRoute = useThrottledCallback(selectActiveTabByRoute, 1000);

  function selectLastAuthContext(matches: MatchesType) {
    const matchedWithAuth = matches.filter(match => match.context.auth);
    // console.debug("TabsGroup auth context matches:", matchedWithAuth);
    return matchedWithAuth.at(-1)?.context;
  }
  function selectTabMatches(matches: MatchesType) {
    const matchedWithPathname = matches.filter(match => match.pathname);
    // console.debug('TabsGroup pathname matches:', matchedWithPathname);
    return matchedWithPathname;
  }
  const matchedWithPathname = useMatches({
    select: selectTabMatches,
  });
  const lastMatchContext = useMatches({
    select: selectLastAuthContext,
  });
  useMemo<void>(() =>
    throttledSelectActiveTabByRoute(
      props.tabs,
      matchedWithPathname,
    ),
  [props.tabs],
  );

  const navigate = useNavigate();
  const [
    rootTabsRef,
    setTabsRootRef,
  ] = useState<HTMLDivElement | null>(null);
  const [
    tabsRefs,
    setTabsRefs,
  ] = useState<Record<string, HTMLButtonElement | null>>({});
  const setTabRef = (val: string) => (node: HTMLButtonElement) => {
    tabsRefs[val] = node;
    setTabsRefs(tabsRefs);
  };
  const tabsList = useMemo<React.ReactNode>(() => {
    return props.tabs.map((tab, tabIndex) => {
      let _show = true;
      if (tab.hidden instanceof Function) {
        _show = tab.hidden(lastMatchContext);
      } else if (tab.hidden) {
        _show = false;
      }
      const tabValue = String(tabIndex + 1);
      return (
        <Tabs.Tab
          key={ tabValue }
          value={ tabValue }
          ref={ setTabRef(String(parseInt(tabValue) - 1)) }
          className={ tabsGroupStyles.tab }
        >
          { tab.label }
        </Tabs.Tab>
      );
    });
  }, [
    props.tabs,
    lastMatchContext,
  ]);

  function tabSelected(selectedValue: number | string | null) {
    if (selectedValue) {
      console.debug(selectedValue);
      if ([
        'number',
        'string',
      ].includes(typeof selectedValue)) {
        let val = typeof selectedValue === 'number' ? selectedValue : parseInt(selectedValue);
        if (val) {
          val = val - 1;
          console.debug(val, props.tabs[val]);
          setValue(val);
          navigate(props.tabs[val].link);
        }
      }
    }
  }

  return (
    <>
      <Tabs
        variant="none"
        value={ String(value) }
        onChange={ tabSelected }
        visibleFrom={ props.visibleFrom || 'md' }
      >
        <Tabs.List
          ref={ setTabsRootRef }
          className={ tabsGroupStyles.list }
          justify="center"
        >
          { tabsList }
          <FloatingIndicator
            target={ tabsRefs[value] }
            parent={ rootTabsRef }
            className={ tabsGroupStyles.indicator }
          />
        </Tabs.List>
      </Tabs>
    </>
  );
}

export default TabsGroup;
